/**
 * Template Mapper Component
 * Main component for mapping fields onto PDF templates
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { PdfViewer, PdfViewerRef, FontInfo, DetectedField } from './PdfViewer';
import { MappingCanvas } from './MappingCanvas';
import { AVAILABLE_FIELDS } from './types';
import { FieldPalette } from './FieldPalette';
import { MappingProperties } from './MappingProperties';
import {
  PdfTemplateWithMappings,
  MappingRegion,
  FieldName,
  PdfTemplateMapping,
} from './types';

interface TemplateMapperProps {
  template: PdfTemplateWithMappings;
  onSave: (mappings: Omit<MappingRegion, 'id' | 'isNew'>[]) => Promise<void>;
  onBack: () => void;
}

const API_BASE = '/api/pdf-templates';

export const TemplateMapper: React.FC<TemplateMapperProps> = ({
  template,
  onSave,
  onBack,
}) => {
  // State
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1);
  const [pageCount, setPageCount] = useState(template.pageCount);
  const [pageDimensions, setPageDimensions] = useState({ width: 612, height: 792 });

  const [mappings, setMappings] = useState<MappingRegion[]>(() =>
    template.mappings.map(m => ({
      id: m.id,
      fieldName: m.fieldName as FieldName,
      fieldType: m.fieldType as MappingRegion['fieldType'],
      pageNumber: m.pageNumber,
      x: m.x,
      y: m.y,
      width: m.width || 100,
      height: m.height || 20,
      fontFamily: m.fontFamily,
      fontSize: m.fontSize,
      fontColor: m.fontColor,
      textAlign: m.textAlign,
      lineHeight: m.lineHeight,
      maxLines: m.maxLines,
      formatPattern: m.formatPattern,
    }))
  );

  const [selectedMapping, setSelectedMapping] = useState<string | null>(null);
  const [drawingField, setDrawingField] = useState<FieldName | null>(null);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [autoDetecting, setAutoDetecting] = useState(false);
  const pdfViewerRef = useRef<PdfViewerRef>(null);

  // Function to get font info for a region
  const getFontInfo = useCallback(async (
    x: number,
    y: number,
    width: number,
    height: number,
    pageNum: number
  ): Promise<FontInfo | null> => {
    if (pdfViewerRef.current) {
      return pdfViewerRef.current.getFontInfoInRegion(x, y, width, height, pageNum);
    }
    return null;
  }, []);

  // Auto-detect fields from PDF using AI
  const handleAutoDetect = useCallback(async () => {
    setAutoDetecting(true);
    try {
      // Call the AI detection endpoint
      const response = await fetch(`/api/pdf-templates/${template.id}/ai-detect`, {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'AI detection failed');
      }

      const data = await response.json();
      const detectedFields = data.detectedFields || [];

      console.log('AI detected fields:', detectedFields);

      if (detectedFields.length === 0) {
        alert('No fields were automatically detected. You may need to map fields manually.');
        return;
      }

      // Convert detected fields to mappings
      const newMappings: MappingRegion[] = [];

      for (const field of detectedFields) {
        // Check if this field name is valid
        const fieldDef = AVAILABLE_FIELDS.find(f => f.name === field.fieldName);
        if (!fieldDef) {
          console.log(`Skipping unknown field: ${field.fieldName}`);
          continue;
        }

        // Check if we already have this field mapped
        const existingMapping = mappings.find(m => m.fieldName === field.fieldName && m.pageNumber === field.pageNumber);
        if (existingMapping) {
          console.log(`Skipping already mapped field: ${field.fieldName}`);
          continue;
        }

        newMappings.push({
          id: `ai_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          fieldName: field.fieldName as FieldName,
          fieldType: field.fieldType,
          pageNumber: field.pageNumber,
          x: field.x,
          y: field.y,
          width: field.width,
          height: field.height,
          fontFamily: 'Helvetica',
          fontSize: 10,
          fontColor: '#000000',
          textAlign: field.fieldType === 'currency' || field.fieldType === 'number' ? 'right' : 'left',
          lineHeight: 1.2,
          isNew: true,
        });
      }

      if (newMappings.length > 0) {
        setMappings(prev => [...prev, ...newMappings]);
        alert(`AI detected ${newMappings.length} field(s). Please review and adjust the positions as needed.`);
      } else {
        alert('All detected fields are already mapped.');
      }
    } catch (err: any) {
      console.error('Auto-detect error:', err);
      alert(err.message || 'Error during AI detection. Please try again.');
    } finally {
      setAutoDetecting(false);
    }
  }, [mappings, template.id]);

  // Track changes
  useEffect(() => {
    const originalIds = new Set(template.mappings.map(m => m.id));
    const hasNew = mappings.some(m => !originalIds.has(m.id));
    const hasDeleted = template.mappings.some(m => !mappings.find(mm => mm.id === m.id));
    const hasModified = mappings.some(m => {
      const orig = template.mappings.find(mm => mm.id === m.id);
      if (!orig) return false;
      return (
        m.x !== orig.x ||
        m.y !== orig.y ||
        m.width !== orig.width ||
        m.height !== orig.height ||
        m.fontFamily !== orig.fontFamily ||
        m.fontSize !== orig.fontSize ||
        m.fontColor !== orig.fontColor ||
        m.textAlign !== orig.textAlign ||
        m.lineHeight !== orig.lineHeight ||
        m.formatPattern !== orig.formatPattern
      );
    });
    setHasChanges(hasNew || hasDeleted || hasModified);
  }, [mappings, template.mappings]);

  // PDF URL
  const pdfUrl = `${API_BASE}/${template.id}/preview`;

  // Get used fields
  const usedFields = mappings.map(m => m.fieldName);

  // Get selected mapping object
  const selectedMappingObj = selectedMapping
    ? mappings.find(m => m.id === selectedMapping) || null
    : null;

  // Handlers
  const handlePageRendered = useCallback((dimensions: { width: number; height: number }) => {
    setPageDimensions(dimensions);
  }, []);

  const handleSelectMapping = useCallback((id: string | null) => {
    setSelectedMapping(id);
    if (id) {
      setDrawingField(null);
    }
  }, []);

  const handleUpdateMapping = useCallback((id: string, updates: Partial<MappingRegion>) => {
    setMappings(prev =>
      prev.map(m => (m.id === id ? { ...m, ...updates } : m))
    );
  }, []);

  const handleCreateMapping = useCallback((mapping: Omit<MappingRegion, 'id'>) => {
    const newMapping: MappingRegion = {
      ...mapping,
      id: `new_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      isNew: true,
    };
    setMappings(prev => [...prev, newMapping]);
    setSelectedMapping(newMapping.id);
    setDrawingField(null);
  }, []);

  const handleDeleteMapping = useCallback((id: string) => {
    setMappings(prev => prev.filter(m => m.id !== id));
    if (selectedMapping === id) {
      setSelectedMapping(null);
    }
  }, [selectedMapping]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const mappingsToSave = mappings.map(m => ({
        fieldName: m.fieldName,
        fieldType: m.fieldType,
        pageNumber: m.pageNumber,
        x: m.x,
        y: m.y,
        width: m.width,
        height: m.height,
        fontFamily: m.fontFamily,
        fontSize: m.fontSize,
        fontColor: m.fontColor,
        textAlign: m.textAlign,
        lineHeight: m.lineHeight,
        maxLines: m.maxLines,
        formatPattern: m.formatPattern,
      }));
      await onSave(mappingsToSave);
      setHasChanges(false);
    } catch (error) {
      console.error('Error saving mappings:', error);
      alert('Failed to save mappings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleZoomIn = () => setScale(s => Math.min(3, s + 0.25));
  const handleZoomOut = () => setScale(s => Math.max(0.25, s - 0.25));
  const handleZoomReset = () => setScale(1);

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Left Sidebar - Field Palette */}
      <div className="w-72 bg-white border-r border-gray-200 flex flex-col">
        <div className="p-4 border-b border-gray-200">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-3"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Templates
          </button>
          <h2 className="text-lg font-semibold text-gray-900">{template.templateName}</h2>
          <p className="text-sm text-gray-500">{template.bankName}</p>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <FieldPalette
            activeField={drawingField}
            onSelectField={setDrawingField}
            usedFields={usedFields}
          />
        </div>
      </div>

      {/* Center - PDF Viewer */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Toolbar */}
        <div className="bg-white border-b border-gray-200 px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Page Navigation */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage <= 1}
                className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <span className="text-sm text-gray-600">
                Page {currentPage} of {pageCount}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(pageCount, p + 1))}
                disabled={currentPage >= pageCount}
                className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            {/* Zoom Controls */}
            <div className="flex items-center gap-2 border-l border-gray-200 pl-4">
              <button
                onClick={handleZoomOut}
                className="p-1 rounded hover:bg-gray-100"
                title="Zoom Out"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
                </svg>
              </button>
              <button
                onClick={handleZoomReset}
                className="px-2 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded"
              >
                {Math.round(scale * 100)}%
              </button>
              <button
                onClick={handleZoomIn}
                className="p-1 rounded hover:bg-gray-100"
                title="Zoom In"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
                </svg>
              </button>
            </div>

            {/* Mapping Count */}
            <div className="text-sm text-gray-500 border-l border-gray-200 pl-4">
              {mappings.length} field{mappings.length !== 1 ? 's' : ''} mapped
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Auto-Detect Button */}
            <button
              onClick={handleAutoDetect}
              disabled={autoDetecting}
              className="px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors bg-purple-600 text-white hover:bg-purple-700 disabled:bg-purple-300 disabled:cursor-not-allowed"
              title="Automatically detect fields from the PDF"
            >
              {autoDetecting ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Scanning...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  Auto-Detect with AI
                </>
              )}
            </button>

            {/* Save Button */}
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className={`
              px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors
              ${hasChanges
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }
            `}
          >
            {saving ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Saving...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Save Mappings
              </>
            )}
          </button>
          </div>
        </div>

        {/* PDF View Area */}
        <div className="flex-1 overflow-auto bg-gray-800 p-8">
          <div
            className="relative mx-auto bg-white shadow-xl"
            style={{
              width: pageDimensions.width * scale,
              height: pageDimensions.height * scale,
            }}
          >
            <PdfViewer
              ref={pdfViewerRef}
              pdfUrl={pdfUrl}
              pageNumber={currentPage}
              scale={scale}
              onPageRendered={handlePageRendered}
              onPageCountChange={setPageCount}
            />
            <MappingCanvas
              width={pageDimensions.width}
              height={pageDimensions.height}
              scale={scale}
              mappings={mappings}
              selectedMapping={selectedMapping}
              drawingField={drawingField}
              onSelectMapping={handleSelectMapping}
              onUpdateMapping={handleUpdateMapping}
              onCreateMapping={handleCreateMapping}
              onDeleteMapping={handleDeleteMapping}
              pageNumber={currentPage}
              getFontInfo={getFontInfo}
            />
          </div>
        </div>
      </div>

      {/* Right Sidebar - Properties */}
      <div className="w-80 bg-white border-l border-gray-200 flex flex-col">
        <div className="p-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900">Properties</h3>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          <MappingProperties
            mapping={selectedMappingObj}
            onChange={(updates) => {
              if (selectedMapping) {
                handleUpdateMapping(selectedMapping, updates);
              }
            }}
            onDelete={() => {
              if (selectedMapping) {
                handleDeleteMapping(selectedMapping);
              }
            }}
            pageCount={pageCount}
          />
        </div>
      </div>
    </div>
  );
};

export default TemplateMapper;
